// ===================================================================
// PA GRANTS — Contracts
// Full CRUD with list view, tabbed create/edit modal, delete confirmation
// Uses direct API calls with TABLES/FIELDS constants
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Textarea,
  Select, ActionIcon, Modal, Stack, SimpleGrid, Table, Loader, Center, Tabs,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
  IconFileText, IconCash, IconShieldCheck, IconCalendar,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { TABLES, FIELDS, fetchTable, createRecord, updateRecord, deleteRecord } from '../../lib/api-switch';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { fmtDate } from '../../lib/date-format';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Helpers ──────────────────────────────────────────────────

const s = (row: Record<string, unknown>, ...keys: string[]): string => {
  for (const k of keys) { const v = row[k]; if (v != null && v !== '') return String(v); }
  return '';
};

const fmt = (v: string) => {
  const n = parseFloat(v);
  return isNaN(n) ? '\u2014' : '$' + n.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const F = FIELDS.paContracts;
const T = TABLES.paContracts;

const YES_NO = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: 'fixed_price', label: 'Fixed Price' },
  { value: 'time_and_materials', label: 'Time & Materials' },
  { value: 'unit_price', label: 'Unit Price' },
  { value: 'cost_plus', label: 'Cost Plus' },
];

const PROCUREMENT_OPTIONS = [
  { value: 'competitive_sealed', label: 'Competitive Sealed Bids' },
  { value: 'competitive_negotiation', label: 'Competitive Negotiation' },
  { value: 'small_purchase', label: 'Small Purchase' },
  { value: 'sole_source', label: 'Sole Source' },
  { value: 'emergency', label: 'Emergency' },
];

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'complete', label: 'Complete' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'disputed', label: 'Disputed' },
];

const STATUS_COLORS: Record<string, string> = {
  draft: '#939598', active: 'var(--color-success)', complete: 'var(--color-primary-700)', terminated: 'var(--color-error)', disputed: '#F86A0B',
};

interface ContractForm {
  projectId: string; organizationId: string;
  // Basic
  contractorName: string; contractorUei: string; contractNumber: string;
  contractType: string; procurementMethod: string; contractAmount: string; scopeOfWork: string;
  // Financial
  changeOrderCount: string; changeOrderAmount: string; totalContractValue: string;
  amountPaid: string; retainageHeld: string;
  // Compliance
  costPriceAnalysis: string; conflictOfInterestCheck: string;
  davisBaconApplicable: string; davisBaconCertified: string;
  debarmentChecked: string; debarmentCheckDate: string;
  bondingRequired: string; bondAmount: string;
  competitiveBidsReceived: string; biddersListOnFile: string;
  contractProvisionsIncluded: string; procurementDocsOnFile: string;
  soleSrcJustificationOnFile: string;
  // Dates
  startDate: string; endDate: string; status: string; notes: string;
}

const EMPTY: ContractForm = {
  projectId: '', organizationId: '',
  contractorName: '', contractorUei: '', contractNumber: '',
  contractType: 'fixed_price', procurementMethod: 'competitive_sealed',
  contractAmount: '', scopeOfWork: '',
  changeOrderCount: '', changeOrderAmount: '', totalContractValue: '',
  amountPaid: '', retainageHeld: '',
  costPriceAnalysis: 'false', conflictOfInterestCheck: 'false',
  davisBaconApplicable: 'false', davisBaconCertified: 'false',
  debarmentChecked: 'false', debarmentCheckDate: '',
  bondingRequired: 'false', bondAmount: '',
  competitiveBidsReceived: '', biddersListOnFile: 'false',
  contractProvisionsIncluded: 'false', procurementDocsOnFile: 'false',
  soleSrcJustificationOnFile: 'false',
  startDate: '', endDate: '', status: 'draft', notes: '',
};

// ── Component ────────────────────────────────────────────────

interface ContractsProps {
  projectId?: string;
}

export default function Contracts({ projectId: filterProjectId }: ContractsProps = {}) {
  const { canEdit } = useModulePermission('pagrants');
  const { projects, organizations, fetchProjects, fetchOrganizations } = usePAGrantsStore();

  const [items, setItems] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<Record<string, unknown> | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Record<string, unknown> | null>(null);
  const [form, setForm] = useState<ContractForm>({ ...EMPTY });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('basic');
  const [detailTarget, setDetailTarget] = useState<Record<string, unknown> | null>(null);
  const PAGE_SIZE = 25;
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  function toggleSort(field: string) {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }
  function sortIcon(field: string) {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  const load = async () => {
    setLoading(true);
    try {
      const rows = await fetchTable(T);
      setItems(rows.map((r: Record<string, unknown>) => {
        const mapped: Record<string, unknown> = { record_id: r.record_id, id: r.record_id, Rid: r.record_id };
        if (F) {
          for (const [k, fieldId] of Object.entries(F as Record<string, string>)) {
            mapped[k] = r[fieldId];
          }
        }
        return mapped;
      }));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); fetchProjects(); fetchOrganizations(); }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const projectOptions = useMemo(() =>
    projects.map(p => ({ value: String(p.id), label: `${p.projectNumber || 'No #'} \u2014 ${p.projectTitle || 'Untitled'}` })),
    [projects]);

  const orgOptions = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.orgName || `Org ${o.id}` })),
    [organizations]);

  const filtered = useMemo(() => {
    let list = items;
    if (filterProjectId) {
      list = list.filter(r => s(r, 'projectId', 'project_id') === filterProjectId);
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        s(r, 'contractorName', 'contractor_name').toLowerCase().includes(q) ||
        s(r, 'contractNumber', 'contract_number').toLowerCase().includes(q) ||
        s(r, 'contractType', 'contract_type').toLowerCase().includes(q),
      );
    }
    if (sortField) {
      list = [...list].sort((a, b) => {
        const av = String((a as any)[sortField] ?? '');
        const bv = String((b as any)[sortField] ?? '');
        const num = !isNaN(Number(av)) && !isNaN(Number(bv));
        const cmp = num ? Number(av) - Number(bv) : av.localeCompare(bv);
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return list;
  }, [items, search, filterProjectId, sortField, sortDir]);

  useEffect(() => { setPage(1); }, [filtered.length]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const setField = (key: keyof ContractForm, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => { setEditTarget(null); setForm({ ...EMPTY, projectId: filterProjectId || '' }); setActiveTab('basic'); setModalOpen(true); };
  const openEdit = (row: Record<string, unknown>) => {
    setEditTarget(row);
    setForm({
      projectId: s(row, 'projectId', 'project_id'),
      organizationId: s(row, 'organizationId', 'organization_id'),
      contractorName: s(row, 'contractorName', 'contractor_name'),
      contractorUei: s(row, 'contractorUei', 'contractor_uei'),
      contractNumber: s(row, 'contractNumber', 'contract_number'),
      contractType: s(row, 'contractType', 'contract_type') || 'fixed_price',
      procurementMethod: s(row, 'procurementMethod', 'procurement_method') || 'competitive_sealed',
      contractAmount: s(row, 'contractAmount', 'contract_amount'),
      scopeOfWork: s(row, 'scopeOfWork', 'scope_of_work'),
      changeOrderCount: s(row, 'changeOrderCount', 'change_order_count'),
      changeOrderAmount: s(row, 'changeOrderAmount', 'change_order_amount'),
      totalContractValue: s(row, 'totalContractValue', 'total_contract_value'),
      amountPaid: s(row, 'amountPaid', 'amount_paid'),
      retainageHeld: s(row, 'retainageHeld', 'retainage_held'),
      costPriceAnalysis: s(row, 'costPriceAnalysis', 'cost_price_analysis') || 'false',
      conflictOfInterestCheck: s(row, 'conflictOfInterestCheck', 'conflict_of_interest_check') || 'false',
      davisBaconApplicable: s(row, 'davisBaconApplicable', 'davis_bacon_applicable') || 'false',
      davisBaconCertified: s(row, 'davisBaconCertified', 'davis_bacon_certified') || 'false',
      debarmentChecked: s(row, 'debarmentChecked', 'debarment_checked') || 'false',
      debarmentCheckDate: s(row, 'debarmentCheckDate', 'debarment_check_date'),
      bondingRequired: s(row, 'bondingRequired', 'bonding_required') || 'false',
      bondAmount: s(row, 'bondAmount', 'bond_amount'),
      competitiveBidsReceived: s(row, 'competitiveBidsReceived', 'competitive_bids_received'),
      biddersListOnFile: s(row, 'biddersListOnFile', 'bidders_list_on_file') || 'false',
      contractProvisionsIncluded: s(row, 'contractProvisionsIncluded', 'contract_provisions_included') || 'false',
      procurementDocsOnFile: s(row, 'procurementDocsOnFile', 'procurement_docs_on_file') || 'false',
      soleSrcJustificationOnFile: s(row, 'soleSrcJustificationOnFile', 'sole_src_justification_on_file') || 'false',
      startDate: s(row, 'startDate', 'start_date'),
      endDate: s(row, 'endDate', 'end_date'),
      status: s(row, 'status') || 'draft',
      notes: s(row, 'notes'),
    });
    setActiveTab('basic');
    setModalOpen(true);
  };

  const buildFields = (): Array<[string, string]> => {
    const f: Array<[string, string]> = [];
    if (form.projectId) f.push([F.projectId, form.projectId]);
    if (form.organizationId) f.push([F.organizationId, form.organizationId]);
    if (form.contractorName) f.push([F.contractorName, form.contractorName]);
    if (form.contractorUei) f.push([F.contractorUei, form.contractorUei]);
    if (form.contractNumber) f.push([F.contractNumber, form.contractNumber]);
    if (form.contractType) f.push([F.contractType, form.contractType]);
    if (form.procurementMethod) f.push([F.procurementMethod, form.procurementMethod]);
    if (form.contractAmount) f.push([F.contractAmount, form.contractAmount]);
    if (form.scopeOfWork) f.push([F.scopeOfWork, form.scopeOfWork]);
    if (form.changeOrderCount) f.push([F.changeOrderCount, form.changeOrderCount]);
    if (form.changeOrderAmount) f.push([F.changeOrderAmount, form.changeOrderAmount]);
    if (form.totalContractValue) f.push([F.totalContractValue, form.totalContractValue]);
    if (form.amountPaid) f.push([F.amountPaid, form.amountPaid]);
    if (form.retainageHeld) f.push([F.retainageHeld, form.retainageHeld]);
    if (form.costPriceAnalysis) f.push([F.costPriceAnalysis, form.costPriceAnalysis]);
    if (form.conflictOfInterestCheck) f.push([F.conflictOfInterestCheck, form.conflictOfInterestCheck]);
    if (form.davisBaconApplicable) f.push([F.davisBaconApplicable, form.davisBaconApplicable]);
    if (form.davisBaconCertified) f.push([F.davisBaconCertified, form.davisBaconCertified]);
    if (form.debarmentChecked) f.push([F.debarmentChecked, form.debarmentChecked]);
    if (form.debarmentCheckDate) f.push([F.debarmentCheckDate, form.debarmentCheckDate]);
    if (form.bondingRequired) f.push([F.bondingRequired, form.bondingRequired]);
    if (form.bondAmount) f.push([F.bondAmount, form.bondAmount]);
    if (form.competitiveBidsReceived) f.push([F.competitiveBidsReceived, form.competitiveBidsReceived]);
    if (form.biddersListOnFile) f.push([F.biddersListOnFile, form.biddersListOnFile]);
    if (form.contractProvisionsIncluded) f.push([F.contractProvisionsIncluded, form.contractProvisionsIncluded]);
    if (form.procurementDocsOnFile) f.push([F.procurementDocsOnFile, form.procurementDocsOnFile]);
    if (form.soleSrcJustificationOnFile) f.push([F.soleSrcJustificationOnFile, form.soleSrcJustificationOnFile]);
    if (form.startDate) f.push([F.startDate, form.startDate]);
    if (form.endDate) f.push([F.endDate, form.endDate]);
    if (form.status) f.push([F.status, form.status]);
    if (form.notes) f.push([F.notes, form.notes]);
    return f;
  };

  const handleSave = async () => {
    if (!form.contractorName.trim()) {
      notifications.show({ message: 'Contractor name is required', color: '#C68700' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateRecord(T, Number(editTarget.id) || 0, buildFields());
        notifications.show({ message: 'Contract updated', color: 'var(--color-success)' });
      } else {
        await createRecord(T, buildFields());
        notifications.show({ message: 'Contract created', color: 'var(--color-success)' });
      }
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteRecord(T, Number(deleteTarget.id) || 0);
      notifications.show({ message: 'Contract deleted', color: '#939598' });
      setDeleteTarget(null);
      await load();
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'var(--color-error)' });
    } finally { setSaving(false); }
  };

  if (loading && items.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  const typeLabel = (v: string) => CONTRACT_TYPE_OPTIONS.find(o => o.value === v)?.label ?? v;
  const procLabel = (v: string) => PROCUREMENT_OPTIONS.find(o => o.value === v)?.label ?? v;

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Contracts</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loading} onClick={() => load()}>Refresh</Button>
          {canEdit && <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Contract</Button>}
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search by contractor, contract #, type..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.target.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} contract{filtered.length !== 1 ? 's' : ''}</Text>

      {filtered.length === 0 ? (
        <Paper p="md" radius="md"><Text c="dimmed">No contracts found.</Text></Paper>
      ) : (
        <Paper withBorder>
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
            <Table striped highlightOnHover>
              <Table.Thead style={{ position: 'sticky', top: 0, zIndex: 1, background: '#F8F8F8' }}>
                <Table.Tr>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('contractorName')}>Contractor Name{sortIcon('contractorName')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('contractNumber')}>Contract #{sortIcon('contractNumber')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('contractType')}>Type{sortIcon('contractType')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('procurementMethod')}>Procurement{sortIcon('procurementMethod')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('contractAmount')}>Amount{sortIcon('contractAmount')}</Table.Th>
                  <Table.Th style={{ cursor: 'pointer' }} onClick={() => toggleSort('status')}>Status{sortIcon('status')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pageRows.map(r => {
                  const st = s(r, 'status') || 'draft';
                  return (
                    <Table.Tr key={String(r.id)} onClick={() => setDetailTarget(r)} style={{ cursor: 'pointer' }}>
                      <Table.Td fw={500}>{s(r, 'contractorName', 'contractor_name') || '\u2014'}</Table.Td>
                      <Table.Td>{s(r, 'contractNumber', 'contract_number') || '\u2014'}</Table.Td>
                      <Table.Td>{typeLabel(s(r, 'contractType', 'contract_type'))}</Table.Td>
                      <Table.Td>{procLabel(s(r, 'procurementMethod', 'procurement_method'))}</Table.Td>
                      <Table.Td>{fmt(s(r, 'contractAmount', 'contract_amount'))}</Table.Td>
                      <Table.Td>
                        <Badge size="sm" color={STATUS_COLORS[st] ?? '#939598'}>{st}</Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap={4} justify="flex-end">
                          {canEdit && (
                            <ActionIcon size="sm" variant="subtle" onClick={e => { e.stopPropagation(); openEdit(r); }}>
                              <IconPencil size={14} />
                            </ActionIcon>
                          )}
                          {canEdit && (
                            <ActionIcon size="sm" variant="subtle" color="var(--color-error)" onClick={e => { e.stopPropagation(); setDeleteTarget(r); }}>
                              <IconTrash size={14} />
                            </ActionIcon>
                          )}
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </div>
        </Paper>
      )}

      {totalPages > 1 && (
        <Group justify="center" mt="sm" gap="xs">
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(1)}>««</Button>
          <Button size="xs" variant="subtle" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>‹ Prev</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const p2 = start + i;
            return p2 <= totalPages ? (
              <Button key={p2} size="xs" variant={p2 === page ? 'filled' : 'subtle'} onClick={() => setPage(p2)}>{p2}</Button>
            ) : null;
          })}
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next ›</Button>
          <Button size="xs" variant="subtle" disabled={page === totalPages} onClick={() => setPage(totalPages)}>»»</Button>
          <Text size="xs" c="dimmed" ml="xs">{filtered.length} total</Text>
        </Group>
      )}

      {/* ── Create / Edit Modal (Tabbed) ─────────────────────── */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Contract' : 'Add Contract'} centered size="xl">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Project" data={projectOptions} searchable clearable
              value={form.projectId} onChange={v => setField('projectId', v ?? '')} />
            <Select label="Organization" data={orgOptions} searchable clearable
              value={form.organizationId} onChange={v => setField('organizationId', v ?? '')} />
          </SimpleGrid>

          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List mb="md">
              <Tabs.Tab value="basic" leftSection={<IconFileText size={14} />}>Basic</Tabs.Tab>
              <Tabs.Tab value="financial" leftSection={<IconCash size={14} />}>Financial</Tabs.Tab>
              <Tabs.Tab value="compliance" leftSection={<IconShieldCheck size={14} />}>Compliance</Tabs.Tab>
              <Tabs.Tab value="dates" leftSection={<IconCalendar size={14} />}>Dates</Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="basic">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Contractor Name" required value={form.contractorName}
                  onChange={e => setField('contractorName', e.target.value)} />
                <TextInput label="Contractor UEI" value={form.contractorUei}
                  onChange={e => setField('contractorUei', e.target.value)} />
                <TextInput label="Contract Number" value={form.contractNumber}
                  onChange={e => setField('contractNumber', e.target.value)} />
                <Select label="Contract Type" data={CONTRACT_TYPE_OPTIONS}
                  value={form.contractType} onChange={v => setField('contractType', v ?? 'fixed_price')} />
                <Select label="Procurement Method" data={PROCUREMENT_OPTIONS}
                  value={form.procurementMethod} onChange={v => setField('procurementMethod', v ?? 'competitive_sealed')} />
                <TextInput label="Contract Amount ($)" value={form.contractAmount}
                  onChange={e => setField('contractAmount', e.target.value)} />
              </SimpleGrid>
              <Textarea label="Scope of Work" mt="sm" value={form.scopeOfWork}
                onChange={e => setField('scopeOfWork', e.target.value)} autosize minRows={3} />
            </Tabs.Panel>

            <Tabs.Panel value="financial">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Change Order Count" value={form.changeOrderCount}
                  onChange={e => setField('changeOrderCount', e.target.value)} />
                <TextInput label="Change Order Amount ($)" value={form.changeOrderAmount}
                  onChange={e => setField('changeOrderAmount', e.target.value)} />
                <TextInput label="Total Contract Value ($)" value={form.totalContractValue}
                  onChange={e => setField('totalContractValue', e.target.value)} />
                <TextInput label="Amount Paid ($)" value={form.amountPaid}
                  onChange={e => setField('amountPaid', e.target.value)} />
                <TextInput label="Retainage Held ($)" value={form.retainageHeld}
                  onChange={e => setField('retainageHeld', e.target.value)} />
              </SimpleGrid>
            </Tabs.Panel>

            <Tabs.Panel value="compliance">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <Select label="Cost/Price Analysis" data={YES_NO}
                  value={form.costPriceAnalysis} onChange={v => setField('costPriceAnalysis', v ?? 'false')} />
                <Select label="Conflict of Interest Check" data={YES_NO}
                  value={form.conflictOfInterestCheck} onChange={v => setField('conflictOfInterestCheck', v ?? 'false')} />
                <Select label="Davis-Bacon Applicable" data={YES_NO}
                  value={form.davisBaconApplicable} onChange={v => setField('davisBaconApplicable', v ?? 'false')} />
                <Select label="Davis-Bacon Certified" data={YES_NO}
                  value={form.davisBaconCertified} onChange={v => setField('davisBaconCertified', v ?? 'false')} />
                <Select label="Debarment Checked" data={YES_NO}
                  value={form.debarmentChecked} onChange={v => setField('debarmentChecked', v ?? 'false')} />
                <TextInput label="Debarment Check Date" type="date" value={form.debarmentCheckDate}
                  onChange={e => setField('debarmentCheckDate', e.target.value)} />
                <Select label="Bonding Required" data={YES_NO}
                  value={form.bondingRequired} onChange={v => setField('bondingRequired', v ?? 'false')} />
                {form.bondingRequired === 'true' && (
                  <TextInput label="Bond Amount ($)" value={form.bondAmount}
                    onChange={e => setField('bondAmount', e.target.value)} />
                )}
                <TextInput label="Competitive Bids Received" value={form.competitiveBidsReceived}
                  onChange={e => setField('competitiveBidsReceived', e.target.value)} />
                <Select label="Bidders List on File" data={YES_NO}
                  value={form.biddersListOnFile} onChange={v => setField('biddersListOnFile', v ?? 'false')} />
                <Select label="Contract Provisions Included" data={YES_NO}
                  value={form.contractProvisionsIncluded} onChange={v => setField('contractProvisionsIncluded', v ?? 'false')} />
                <Select label="Procurement Docs on File" data={YES_NO}
                  value={form.procurementDocsOnFile} onChange={v => setField('procurementDocsOnFile', v ?? 'false')} />
                <Select label="Sole Source Justification on File" data={YES_NO}
                  value={form.soleSrcJustificationOnFile} onChange={v => setField('soleSrcJustificationOnFile', v ?? 'false')} />
              </SimpleGrid>
            </Tabs.Panel>

            <Tabs.Panel value="dates">
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextInput label="Start Date" type="date" value={form.startDate}
                  onChange={e => setField('startDate', e.target.value)} />
                <TextInput label="End Date" type="date" value={form.endDate}
                  onChange={e => setField('endDate', e.target.value)} />
                <Select label="Status" data={STATUS_OPTIONS}
                  value={form.status} onChange={v => setField('status', v ?? 'draft')} />
              </SimpleGrid>
              <Textarea label="Notes" mt="sm" value={form.notes}
                onChange={e => setField('notes', e.target.value)} autosize minRows={2} />
            </Tabs.Panel>
          </Tabs>

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            {canEdit && (
              <Button loading={saving} onClick={handleSave}>
                {editTarget ? 'Save Changes' : 'Create Contract'}
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>

      {/* ── Delete Confirmation ──────────────────────────────── */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        title="Delete Contract" centered size="sm">
        <Stack>
          <Text size="sm">
            Delete contract for <strong>{deleteTarget ? s(deleteTarget, 'contractorName', 'contractor_name') : ''}</strong>? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            {canEdit && <Button color="var(--color-error)" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>}
          </Group>
        </Stack>
      </Modal>

      {/* ── Detail View Modal ──────────────────────────────── */}
      <Modal opened={!!detailTarget} onClose={() => setDetailTarget(null)}
        title={detailTarget ? s(detailTarget, 'contractorName', 'contractor_name') : 'Contract Detail'} centered size="lg">
        {detailTarget && (
          <Stack gap="md">
            <SimpleGrid cols={{ base: 1, sm: 2 }}>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Contractor</Text>
                <Text size="sm" fw={500}>{s(detailTarget, 'contractorName', 'contractor_name') || '—'}</Text>
                <Text size="xs" c="dimmed">UEI: {s(detailTarget, 'contractorUei', 'contractor_uei') || '—'}</Text>
                <Text size="xs" c="dimmed">Contract #: {s(detailTarget, 'contractNumber', 'contract_number') || '—'}</Text>
              </Paper>
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Contract Terms</Text>
                <Text size="sm">Type: {typeLabel(s(detailTarget, 'contractType', 'contract_type'))}</Text>
                <Text size="sm">Procurement: {procLabel(s(detailTarget, 'procurementMethod', 'procurement_method'))}</Text>
                <Text size="sm">Status: <Badge size="xs" color={STATUS_COLORS[s(detailTarget, 'status') || 'draft'] ?? '#939598'}>{s(detailTarget, 'status') || 'draft'}</Badge></Text>
              </Paper>
            </SimpleGrid>

            <Paper p="sm" withBorder>
              <Text size="xs" c="dimmed" fw={600} mb={4}>Financial Summary</Text>
              <SimpleGrid cols={{ base: 1, sm: 3 }}>
                <div>
                  <Text size="xs" c="dimmed">Contract Amount</Text>
                  <Text size="sm" fw={600}>{fmt(s(detailTarget, 'contractAmount', 'contract_amount'))}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Change Orders ({s(detailTarget, 'changeOrderCount', 'change_order_count') || '0'})</Text>
                  <Text size="sm" fw={600}>{fmt(s(detailTarget, 'changeOrderAmount', 'change_order_amount'))}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Total Value</Text>
                  <Text size="sm" fw={600}>{fmt(s(detailTarget, 'totalContractValue', 'total_contract_value'))}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Amount Paid</Text>
                  <Text size="sm">{fmt(s(detailTarget, 'amountPaid', 'amount_paid'))}</Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Retainage Held</Text>
                  <Text size="sm">{fmt(s(detailTarget, 'retainageHeld', 'retainage_held'))}</Text>
                </div>
              </SimpleGrid>
            </Paper>

            {s(detailTarget, 'scopeOfWork', 'scope_of_work') && (
              <Paper p="sm" withBorder>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Scope of Work</Text>
                <Text size="sm">{s(detailTarget, 'scopeOfWork', 'scope_of_work')}</Text>
              </Paper>
            )}

            <Paper p="sm" withBorder>
              <Text size="xs" c="dimmed" fw={600} mb={4}>Compliance Checklist</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {[
                  ['Cost/Price Analysis', s(detailTarget, 'costPriceAnalysis', 'cost_price_analysis')],
                  ['Conflict of Interest', s(detailTarget, 'conflictOfInterestCheck', 'conflict_of_interest_check')],
                  ['Davis-Bacon Applicable', s(detailTarget, 'davisBaconApplicable', 'davis_bacon_applicable')],
                  ['Davis-Bacon Certified', s(detailTarget, 'davisBaconCertified', 'davis_bacon_certified')],
                  ['Debarment Checked', s(detailTarget, 'debarmentChecked', 'debarment_checked')],
                  ['Bonding Required', s(detailTarget, 'bondingRequired', 'bonding_required')],
                  ['Bidders List on File', s(detailTarget, 'biddersListOnFile', 'bidders_list_on_file')],
                  ['Provisions Included', s(detailTarget, 'contractProvisionsIncluded', 'contract_provisions_included')],
                  ['Procurement Docs', s(detailTarget, 'procurementDocsOnFile', 'procurement_docs_on_file')],
                ].map(([label, val]) => (
                  <Group key={label as string} gap="xs">
                    <Badge size="xs" color={val === 'true' ? 'var(--color-success)' : 'var(--color-error)'} variant="filled" circle>{val === 'true' ? '✓' : '✗'}</Badge>
                    <Text size="xs">{label}</Text>
                  </Group>
                ))}
              </SimpleGrid>
              {s(detailTarget, 'competitiveBidsReceived', 'competitive_bids_received') && (
                <Text size="xs" c="dimmed" mt="xs">Competitive Bids Received: {s(detailTarget, 'competitiveBidsReceived', 'competitive_bids_received')}</Text>
              )}
            </Paper>

            <Group gap="xs">
              <Text size="xs" c="dimmed">{fmtDate(s(detailTarget, 'startDate', 'start_date'))} → {fmtDate(s(detailTarget, 'endDate', 'end_date'))}</Text>
            </Group>
            {s(detailTarget, 'notes') && (
              <Paper p="sm" withBorder style={{ background: '#F8F8F8' }}>
                <Text size="xs" c="dimmed" fw={600} mb={4}>Notes</Text>
                <Text size="xs">{s(detailTarget, 'notes')}</Text>
              </Paper>
            )}
            <Group justify="flex-end">
              <Button size="xs" variant="light" onClick={() => { setDetailTarget(null); openEdit(detailTarget); }}>Edit</Button>
              <Button size="xs" variant="default" onClick={() => setDetailTarget(null)}>Close</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
